import React, { useState, isValidElement } from 'react';
import { TableRow, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Fields from 'components/Fields';
import Icons from 'components/Icons';
import { FieldPercent, TooltipIcon } from 'components/Base';
import { formatPercent } from 'utils';
import TableComponents from 'components/Table';
import ModelPortfolioAllocations from 'components/ModelPortfolio/ModelPortfolioAllocations';
import EditablePercent from 'components/Base/EditablePercent/EditablePercent';
import { getOverriddenCharges } from 'components/Policies/utils';
import Checkbox from 'components/Checkbox/Checkbox';

const useStyles = makeStyles({
  footer: {
    backgroundColor: '#c6e0f4',
    lineHeight: 'unset'
  },
  icon: {
    fontSize: '1.5rem',
    cursor: 'pointer',
    transition: '0.3s'
  },
  boldRow: {
    fontWeight: 'bold',
    '& input': {
      fontWeight: 'bold'
    }
  },
  iconsBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  }
});

interface FundProps {
  fund: any;
  removeFund: () => void;
  existing_weighting: number;
  sectorName: string;
  modelPortfolio: any;
  overrides: any;
  name: string;
  isReadOnly: boolean;
  isFundActive: boolean;
  risk: any;
  ocf: number;
  tradingCosts: number;
  isChecked?: boolean;
  onToggleCheck?: Function;
  isEvalue: boolean;
}

const Fund = ({
  fund,
  removeFund,
  existing_weighting,
  sectorName,
  modelPortfolio,
  overrides,
  name,
  isReadOnly,
  isFundActive,
  risk,
  ocf,
  tradingCosts,
  isChecked = false,
  // eslint-disable-next-line
  onToggleCheck = () => {},
  isEvalue = false
}: FundProps) => {
  const [isModelPortfolioExpanded, setModelPortfolioExpanded] = useState(false);
  const classes = useStyles();
  const isModelPortfolio = !!modelPortfolio;
  const isWrapCash = !isModelPortfolio && name === 'Wrap Cash';

  const { fundOcf, fundTradingCosts, customTotalFundCharge } =
    getOverriddenCharges(overrides, modelPortfolio, ocf, tradingCosts);

  let growthAssetPercentage = '-' as any;
  let comfortScore = '-' as any;

  if (!isEvalue) {
    if (isValidElement(risk)) {
      growthAssetPercentage = risk;
      comfortScore = risk;
    } else {
      const values = Object.values(risk)[0] as any;
      growthAssetPercentage =
        formatPercent(values?.growth_asset_percentage) || '-';
      comfortScore = `${values.ok_risk?.low} - ${values.ok_risk?.high}`;
    }
  }

  return (
    <>
      <TableRow>
        <TableComponents.TableCell style={{ width: '10px' }}>
          <Checkbox isChecked={!!isChecked} onClick={() => onToggleCheck()} />
        </TableComponents.TableCell>
        <TableComponents.TableCell
          className={isModelPortfolio ? classes.boldRow : ''}
        >
          {name}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          className={isModelPortfolio ? classes.boldRow : ''}
          width="220px"
        >
          {sectorName || '-'}
        </TableComponents.TableCell>
        {isEvalue ? (
          <TableComponents.TableCell
            className={isModelPortfolio ? classes.boldRow : ''}
          >
            {isWrapCash ? '' : risk || '-'}
          </TableComponents.TableCell>
        ) : (
          <>
            <TableComponents.TableCell
              className={isModelPortfolio ? classes.boldRow : ''}
            >
              {isWrapCash ? '' : growthAssetPercentage}
            </TableComponents.TableCell>
            <TableComponents.TableCell
              className={isModelPortfolio ? classes.boldRow : ''}
            >
              {isWrapCash ? '' : comfortScore}
            </TableComponents.TableCell>
          </>
        )}
        <TableComponents.TableCell
          className={isModelPortfolio ? classes.boldRow : ''}
        >
          {isWrapCash ? (
            ''
          ) : (
            <>
              {isModelPortfolio || isReadOnly ? (
                <>{formatPercent(Number(fundOcf) * 100)}</>
              ) : (
                <EditablePercent
                  name={`${fund}.fund_charge_overrides[0].ocf`}
                  formName="portfolioBuilder"
                  defaultValue={ocf}
                />
              )}
            </>
          )}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          className={isModelPortfolio ? classes.boldRow : ''}
        >
          {isWrapCash ? (
            ''
          ) : (
            <>
              {isModelPortfolio || isReadOnly ? (
                <>{formatPercent(Number(fundTradingCosts) * 100)}</>
              ) : (
                <EditablePercent
                  name={`${fund}.fund_charge_overrides[0].trading_costs`}
                  formName="portfolioBuilder"
                  defaultValue={tradingCosts}
                />
              )}
            </>
          )}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          className={isModelPortfolio ? classes.boldRow : ''}
        >
          {isWrapCash ? '' : formatPercent(customTotalFundCharge * 100)}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="120px"
          className={isModelPortfolio ? classes.boldRow : ''}
          id="fund-existing-allocation"
        >
          {formatPercent(existing_weighting)}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="120px"
          className={isModelPortfolio ? classes.boldRow : ''}
          id="fund-allocation"
        >
          <FieldPercent
            name={`${fund}.allocation`}
            component={isReadOnly ? Fields.String : Fields.Text}
            clearfieldinform="portfolioBuilder"
          />
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="80px"
          className={isModelPortfolio ? classes.boldRow : ''}
        >
          <div className={classes.iconsBox}>
            {!isFundActive && (
              <TooltipIcon
                text="Fund(s) are no longer supported."
                icon="warn"
              />
            )}
            {isModelPortfolio && (
              <ExpandMoreIcon
                className={classes.icon}
                onClick={() =>
                  setModelPortfolioExpanded(!isModelPortfolioExpanded)
                }
                style={{
                  transform: isModelPortfolioExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0)'
                }}
              />
            )}
            {!isReadOnly && (
              <Tooltip title="Delete fund" arrow>
                <span
                  className={classes.icon}
                  style={{ cursor: 'pointer' }}
                  onClick={removeFund}
                >
                  <Icons.Delete />
                </span>
              </Tooltip>
            )}
          </div>
        </TableComponents.TableCell>
      </TableRow>
      {isModelPortfolio && isModelPortfolioExpanded && (
        <ModelPortfolioAllocations
          id={modelPortfolio.id}
          fundAllocations={modelPortfolio.fund_allocations}
          overrides={overrides}
          baseName={fund}
          formName="portfolioBuilder"
          showPerformance={false}
          showAllocation={false}
          withEmptyColumn
          isReadOnly={isReadOnly}
          isEvalue={isEvalue}
        />
      )}
    </>
  );
};

export default Fund;
